import { Component, Output, EventEmitter, Input, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { CustomQualification } from 'src/app/models/custom-qualification';
import { MasterQualification } from 'src/app/models/master-qualification';
import { QualificationFilter } from 'src/app/models/qualification-filter';
import { QualificationService } from 'src/app/services/qualifications.service';
import { Settings } from 'src/app/services/settings';
import { Subscription } from 'rxjs';

interface QualificationRecord extends QualificationFilter {
    category: 'Master' | 'Custom';
    question: string;
    controlType?: string;
}

@Component({
    selector: 'app-advanced-filter',
    templateUrl: './advanced-filter.component.html',
    styleUrls: ['./advanced-filter.component.css']
})
export class AdvancedFilterComponent implements OnInit, OnDestroy {
    @Output() submitClicked = new EventEmitter<QualificationFilter[]>();
    @Output() cancelClicked = new EventEmitter<void>();
    @Output() resetClicked = new EventEmitter<string>();
    @Input() agencyId: number;

    @ViewChild(MatSort, { static: true }) sort: MatSort;

    chkIncludeCustom = false;
    strErr = '';
    public dataSource: MatTableDataSource<QualificationRecord>;
    public displayedColumns: string[] = ['chk', 'category', 'question', 'response', 'joins'];

    masterQualifications: MasterQualification[] = [];
    customQualifications: CustomQualification[] = [];
    records: QualificationRecord[] = [];

    private subscriptions: Subscription = new Subscription();
    private masterRecords: QualificationRecord[] = []; // Store master records separately

    constructor(private qualificationsService: QualificationService) {
        this.dataSource = new MatTableDataSource([]);
    }

    ngOnInit(): void {
        this.loadMasterQualifications(); // Load master qualifications on initialization
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    loadMasterQualifications() {
        this.qualificationsService.getAllMasterQualifications().subscribe(
            (result) => {
                this.masterQualifications = result;
                this.masterRecords = this.masterQualifications.map(qual => ({
                    chk: false,
                    category: 'Master',
                    question: qual.question,
                    qualificationsId: qual.id,
                    response: '',
                    joins: '',
                }));
                this.buildDataSource();
            },
            (error) => {
                Settings.getInstance().handleError(error, 'Error: unable to retrieve master qualifications data from the service');
            }
        );
    }

    loadCustomQualifications() {
        if (this.agencyId) {
            const customSubscription = this.qualificationsService.getAllCustomQualificationsByAgencyId(this.agencyId).subscribe(
                (result) => {
                    this.customQualifications = result;
                    this.buildDataSource();
                },
                (error) => {
                    Settings.getInstance().handleError(error, 'Error: unable to retrieve custom qualifications data from the service');
                    this.customQualifications = []; // Ensure empty on error
                    this.buildDataSource();
                }
            );
            this.subscriptions.add(customSubscription);
        } else {
            this.customQualifications = [];
            this.buildDataSource();
        }
    }

    buildDataSource() {
        this.records = [...this.masterRecords]; // Start with the persisted master records

        if (this.agencyId && this.chkIncludeCustom && this.customQualifications) {
            const customRecords: QualificationRecord[] = this.customQualifications.map(qual => ({
                chk: false,
                category: 'Custom',
                question: qual.question,
                controlType: qual.controlType,
                qualificationsId: qual.id,
                response: '',
                joins: '',
            }));
            this.records = [...this.records, ...customRecords];
        }

        this.dataSource.data = this.records;
        this.dataSource.sort = this.sort;
    }

    chkCustomClickHandler(event: any) {
        //this.chkIncludeCustom = !this.chkIncludeCustom; // Toggle the flag immediately
        this.loadCustomQualifications(); // Only load/reload custom qualifications
    }

    onShowHandler() {
        if (this.masterQualifications.length === 0) {
            this.loadMasterQualifications();
        } else {
            this.buildDataSource(); // Just rebuild if master data is already loaded
        }
    }

    onRowClicked(row: any) {
        // ...
    }

    doSubmit() {
        const selectedRecords = this.filterCheckedRecords(this.records);
        this.strErr = '';
        if (selectedRecords.length === 0) {
            this.strErr = 'Please select at least one question.';
        }

        for (const rec of selectedRecords) {
            if (!rec.response) {
                this.strErr = 'Each selected question must have a response.';
                break;
            }
        }

        for (let i = 0; i < selectedRecords.length - 1; i++) {
            if (!selectedRecords[i].joins) {
                this.strErr = 'All but the last selected question must have a join value.';
                break;
            }
        }

        if (selectedRecords.length > 0) {
            selectedRecords[selectedRecords.length - 1].joins = null;
        }

        if (this.strErr) {
            return;
        }

        this.submitClicked.emit(selectedRecords);
    }

    doCancel() {
        this.strErr = '';
        this.cancelClicked.emit();
    }

    doReset() {
        this.resetClicked.emit('');
        // Reset only the dynamic properties (chk, response, joins)
        this.records.forEach(rec => {
            rec.chk = false;
            rec.response = '';
            rec.joins = '';
        });
        this.dataSource.data = [...this.records];
    }

    filterCheckedRecords(records: QualificationRecord[]): QualificationFilter[] {
        return records.filter(record => record.chk === true);
    }
    
    getCustomFieldDescription(controlType: string): string {
    switch (controlType) {
      case 'Number':
        return 'Enter a numeric value.';
      case 'YND':
        return 'Select Yes, No, or Don\'t Know.';
      case 'Text':
        return 'Enter free-form text.';
      case 'Date':
        return 'Select a date from the calendar.';
      default:
        return 'Cutom agency response';
    }
  }
}